import React, { useEffect, useState } from 'react';
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import { useIsMobile } from '@/hooks/use-mobile';

interface CanvasControlsProps {
  onAddStage: () => void;
  onClearAll: () => void;
}

const CanvasControls: React.FC<CanvasControlsProps> = ({ onAddStage, onClearAll }) => {
  const isMobile = useIsMobile();
  const [isFloating, setIsFloating] = useState(true);
  
  useEffect(() => {
    // Function to check if parent has fixed class
    const checkParentClass = () => {
      const container = document.querySelector('.canvas-controls-container');
      if (container) {
        setIsFloating(container.classList.contains('fixed'));
      }
    };
    
    // Check initially and when DOM changes might happen
    checkParentClass();
    
    // Create a MutationObserver to watch for class changes on the container
    const observer = new MutationObserver(checkParentClass);
    const container = document.querySelector('.canvas-controls-container');
    
    if (container) {
      observer.observe(container, { attributes: true, attributeFilter: ['class'] });
    }
    
    return () => {
      observer.disconnect();
    };
  }, []);
  
  return (
    <div className="canvas-controls-wrapper relative w-full">
      {/* Center Add Stage button with improved transitions */}
      <div 
        className={`glassmorphism ${isFloating ? 'fixed' : 'absolute'} ${isFloating ? (isMobile ? 'bottom-4' : 'bottom-6') : 'bottom-4'} left-1/2 -translate-x-1/2 ${isMobile ? 'px-3 py-1.5' : 'px-4 py-2'} rounded-full z-[200] shadow-md flex items-center gap-2 transition-all duration-300 animate-fade-in ease-in-out`}
        style={{ 
          background: 'linear-gradient(to right, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.85))',
          backdropFilter: 'blur(8px)',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
        }}
      >
        <Button 
          variant="outline" 
          onClick={onAddStage}
          className={`${isMobile ? 'h-8 w-8' : 'h-9 w-9'} p-0 rounded-full transition-transform hover:scale-110 active:scale-95 duration-200 bg-slate-50/90 hover:bg-slate-100/90`}
          aria-label="Add Stage"
        >
          <Plus className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
        </Button>
      </div>
      
      {/* Bottom right Clear All button with improved transitions */}
      <div className={`${isFloating ? 'fixed' : 'absolute'} ${isFloating ? (isMobile ? 'bottom-4 right-4' : 'bottom-6 right-6') : 'bottom-4 right-4'} z-[200] transition-all duration-300 animate-fade-in ease-in-out`}>
        <Button 
          variant="outline" 
          size="icon"
          className={`${isMobile ? 'h-9 w-9' : 'h-10 w-10'} rounded-full bg-white/80 backdrop-blur-md shadow-md border border-white/20 hover:bg-rose-50 transition-transform hover:scale-110 active:scale-95 duration-200`}
          onClick={() => {
            toast.warning(
              "Are you sure?", 
              {
                description: "This will clear all stages from your journey map.",
                action: {
                  label: "Clear all",
                  onClick: () => {
                    onClearAll();
                    toast.success("Journey map cleared");
                  }
                }
              }
            );
          }}
          aria-label="Clear All"
        >
          <Trash2 className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-rose-500`} />
        </Button>
      </div>
    </div>
  );
};

export default CanvasControls;
